import * as React from "react"
import { X, ChevronDown, Plus } from "lucide-react"
import { Badge } from "./badge"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Select, SelectContent, SelectTrigger, SelectValue } from "./select"
import { Input } from "./input"

export type Option = {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  className?: string
  placeholder?: string
  isSearchable?: boolean
  showAddButton?: boolean
  addButtonText?: string
  onAddButtonClick?: () => void
  maxHeight?: string
  /** scroll = chips inside trigger; wrap = first label +N summary only (chips rendered by parent via MultiSelectChips) */
  chipLayout?: 'scroll' | 'wrap'
  /** Fires when the dropdown opens or closes (use close to commit pipe selection). */
  onOpenChange?: (open: boolean) => void
}

function selectedLabel(options: Option[], value: string): string {
  return options.find((o) => o.value === value)?.label || `Unknown (${value})`
}

const CHIP_CLASS =
  'inline-flex max-w-[14rem] shrink-0 items-center gap-1 rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-1 text-xs text-blueprint-blue'

/** Removable selection chips for full-width labeled rows outside the MultiSelect. */
export function MultiSelectChips({
  options,
  selected,
  onRemove,
  className,
  /** wrap = multi-line; scroll = single horizontal scroll; ellipsis = one fixed row + +N */
  layout = 'wrap',
  /** When ellipsis overflows, e.g. open the parent MultiSelect. */
  onOverflowClick,
}: {
  options: Option[]
  selected: string[]
  onRemove: (value: string) => void
  className?: string
  layout?: 'wrap' | 'scroll' | 'ellipsis'
  onOverflowClick?: () => void
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const measureRef = React.useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(selected.length)

  React.useLayoutEffect(() => {
    if (layout !== 'ellipsis') {
      setVisibleCount(selected.length)
      return
    }
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure || selected.length === 0) {
      setVisibleCount(selected.length)
      return
    }

    const recompute = () => {
      const available = container.clientWidth
      if (available <= 0) {
        setVisibleCount(selected.length)
        return
      }
      const kids = Array.from(measure.children) as HTMLElement[]
      if (kids.length === 0) {
        setVisibleCount(0)
        return
      }
      const gap = 8
      const overflowChip = kids[kids.length - 1]
      const overflowWidth = overflowChip?.offsetWidth ?? 40
      let used = 0
      let fit = 0
      for (let i = 0; i < selected.length; i += 1) {
        const chip = kids[i]
        if (!chip) break
        const next = used + (fit > 0 ? gap : 0) + chip.offsetWidth
        const remaining = selected.length - (i + 1)
        const needOverflow = remaining > 0
        const limit = needOverflow ? available - overflowWidth - gap : available
        if (next > limit) break
        used = next
        fit += 1
      }
      setVisibleCount(Math.max(0, fit))
    }

    recompute()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null
    ro?.observe(container)
    return () => ro?.disconnect()
  }, [layout, selected, options])

  if (selected.length === 0) return null

  if (layout === 'ellipsis') {
    const hidden = Math.max(0, selected.length - visibleCount)
    const visible = selected.slice(0, visibleCount)
    const hiddenLabels = selected
      .slice(visibleCount)
      .map((value) => selectedLabel(options, value))
      .join(', ')

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative flex h-9 min-w-0 w-full flex-nowrap items-center gap-2 overflow-hidden',
          className,
        )}
        data-testid="multi-select-external-chips"
        data-layout="ellipsis"
      >
        {/* Off-screen measure row: all chips + overflow probe */}
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 flex h-0 overflow-hidden opacity-0"
        >
          {selected.map((value) => (
            <span key={value} className={CHIP_CLASS}>
              <span className="truncate">{selectedLabel(options, value)}</span>
              <X className="h-3 w-3 shrink-0" />
            </span>
          ))}
          <span className={CHIP_CLASS}>+99</span>
        </div>

        {visible.map((value) => {
          const label = selectedLabel(options, value)
          return (
            <span key={value} className={CHIP_CLASS}>
              <span className="truncate">{label}</span>
              <button
                type="button"
                className="ml-1 shrink-0 rounded-full outline-none ring-offset-background hover:text-readable-red focus:ring-2 focus:ring-hero-red focus:ring-offset-1"
                onClick={() => onRemove(value)}
                aria-label={`Remove ${label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )
        })}
        {hidden > 0 ? (
          <button
            type="button"
            data-testid="multi-select-chips-overflow"
            title={hiddenLabels}
            aria-label={`${hidden} more selected`}
            onClick={onOverflowClick}
            className={cn(CHIP_CLASS, 'font-semibold hover:bg-[rgba(0,39,63,0.1)]')}
          >
            +{hidden}
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2',
        layout === 'scroll'
          ? 'h-9 flex-nowrap overflow-x-auto overflow-y-hidden scrollbar-thin'
          : 'flex-wrap',
        className,
      )}
      data-testid="multi-select-external-chips"
    >
      {selected.map((value) => {
        const label = selectedLabel(options, value)
        return (
          <span key={value} className={CHIP_CLASS}>
            <span className="truncate">{label}</span>
            <button
              type="button"
              className="ml-1 shrink-0 rounded-full outline-none ring-offset-background hover:text-readable-red focus:ring-2 focus:ring-hero-red focus:ring-offset-1"
              onClick={() => onRemove(value)}
              aria-label={`Remove ${label}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )
      })}
    </div>
  )
}

export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  placeholder = "Select options...",
  isSearchable = true,
  showAddButton = false,
  addButtonText = "Add New",
  onAddButtonClick,
  maxHeight = "200px",
  chipLayout = 'scroll',
  onOpenChange,
  ...props
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isRemovingItem, setIsRemovingItem] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const selectTriggerRef = React.useRef<HTMLButtonElement>(null)
  const optionsListRef = React.useRef<HTMLDivElement>(null)
  const summaryOnly = chipLayout === 'wrap'

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return options
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [options, searchTerm])

  // Handle option selection/deselection
  const handleOptionToggle = (value: string) => {
    const isSelected = selected.includes(value)
    if (isSelected) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Handle search input blur
  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement
    if (relatedTarget && !relatedTarget.closest('[role="listbox"]')) {
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          const activeElement = document.activeElement as HTMLElement
          if (!activeElement?.closest('[role="listbox"]')) {
            setIsOpen(false)
          }
        }
      }, 100)
    }
  }

  // Handle search input key events
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault()
      handleOptionToggle(filteredOptions[0].value)
    }
    
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
      selectTriggerRef.current?.focus()
    }
  }

  // Handle mouse down on search input
  const handleSearchMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }

  const handleDropdownWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const optionsList = optionsListRef.current
    if (!optionsList || optionsList.scrollHeight <= optionsList.clientHeight) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    optionsList.scrollTop += e.deltaY
  }

  // Map vertical wheel to horizontal scroll on the single-line chip row
  const handleChipRowWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollWidth <= el.clientWidth) {
      return
    }
    // Prefer deltaX (trackpad); fall back to deltaY so mouse wheel scrolls chips
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
    if (delta === 0) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    el.scrollLeft += delta
  }

  // Handle add new button click
  const handleAddNew = () => {
    onAddButtonClick?.()
    setIsOpen(false)
    setSearchTerm('')
  }

  // Clear search when select closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  // Handle unselect from badges
  const handleUnselect = (option: string) => {
    setIsRemovingItem(true)
    onChange(selected.filter((s) => s !== option))
    setTimeout(() => setIsRemovingItem(false), 150)
  }

  // Handle keyboard navigation for badges
  const handleBadgeKeyDown = (e: React.KeyboardEvent, option: string) => {
    if (e.key === "Enter" || e.key === "Delete" || e.key === "Backspace") {
      handleUnselect(option)
    }
  }

  const renderInlineChip = (option: string) => {
    const label = selectedLabel(options, option)
    return (
      <Badge
        key={option}
        variant="secondary"
        className="shrink-0 rounded-md border border-transparent bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 font-normal text-blueprint-blue transition-colors hover:border-hero-red-20 hover:bg-hero-red-10 hover:text-readable-red"
      >
        {label}
        <button
          type="button"
          className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-hero-red focus:ring-offset-1 relative z-50"
          onKeyDown={(e) => handleBadgeKeyDown(e, option)}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleUnselect(option)
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleUnselect(option)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
          aria-label={`Remove ${label}`}
        >
          <X className="h-3 w-3 text-heritage-grey hover:text-readable-red" />
        </button>
      </Badge>
    )
  }

  const triggerSummary = () => {
    if (selected.length === 0) {
      return <span className="truncate text-heritage-grey">{placeholder}</span>
    }
    if (!summaryOnly) {
      return selected.map((option) => renderInlineChip(option))
    }
    const first = selectedLabel(options, selected[0])
    const extra = selected.length - 1
    return (
      <span className="flex min-w-0 items-center gap-1.5 truncate text-sm text-blueprint-blue">
        <span className="truncate">{first}</span>
        {extra > 0 ? (
          <span className="shrink-0 rounded-md border border-[#E2E5EA] bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 text-xs font-medium text-heritage-grey">
            +{extra}
          </span>
        ) : null}
      </span>
    )
  }

  return (
    <div className={`relative ${className}`}>
      <Select
        open={isOpen && !isRemovingItem}
        onOpenChange={(open) => {
          if (isRemovingItem && open) {
            return // Prevent opening while removing
          }
          setIsOpen(open)
          onOpenChange?.(open)
        }}
      >
        <SelectTrigger 
          ref={selectTriggerRef}
          className="h-9 text-color-gray-900 text-left overflow-hidden"
        >
          <div
            className={cn(
              "flex w-full min-w-0 items-center gap-1",
              summaryOnly
                ? "overflow-hidden"
                : cn(
                    "flex-nowrap overflow-x-auto overflow-y-hidden",
                    "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
                    "[&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300",
                    "[&::-webkit-scrollbar-track]:bg-transparent",
                  ),
            )}
            onWheel={summaryOnly ? undefined : handleChipRowWheel}
          >
            {triggerSummary()}
          </div>
        </SelectTrigger>
        <SelectContent className="max-h-[min(24rem,calc(100vh-8rem))] overflow-hidden">
          <div className="flex max-h-[min(12rem,calc(100vh-8rem))] flex-col" onWheel={handleDropdownWheel}>
            {/* Search Input */}
            {isSearchable && (
              <div className="p-2 border-b border-[#E2E5EA] flex-shrink-0 h-12">
                <Input
                  ref={searchInputRef}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  onMouseDown={handleSearchMouseDown}
                  className="h-8 text-[13px] placeholder:text-heritage-grey"
                  autoFocus={false}
                />
              </div>
            )}
            
            {/* Options List */}
            <div 
              ref={optionsListRef}
              className="flex-1 overflow-y-auto min-h-0"
              style={{ maxHeight }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const isSelected = selected.includes(option.value)
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 px-2 py-1.5 rounded-sm",
                        "hover:bg-page-bg",
                        "text-text-grey"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleOptionToggle(option.value)
                      }}
                    >
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-[#E2E5EA]",
                          isSelected
                            ? "bg-hero-red border-hero-red text-white"
                            : "bg-white"
                        )}
                      >
                        {isSelected && (
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-[13px]">{option.label}</span>
                    </div>
                  )
                })
              ) : (
                <div className="p-2 text-center text-heritage-grey text-[13px]">
                  {searchTerm ? 'No results found' : 'No options available'}
                </div>
              )}
            </div>

            {/* Add New Button */}
            {showAddButton && onAddButtonClick && (
              <div className="border-t border-[#E2E5EA] p-1 flex-shrink-0 h-10 flex items-center justify-center">
                <Button
                  className="flex items-center text-[13px] hover:bg-page-bg text-hero-red h-8 w-full"
                  variant="ghost"
                  onClick={handleAddNew}
                  type="button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {addButtonText}
                </Button>
              </div>
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  )
} 